import { AREA_CALCULATION_API_URL } from '../config/api';

/**
 * Calculate area of a polygon on Earth's surface
 * Uses planar approximation with latitude correction (accurate for small areas)
 * @param {Array} coordinates - Array of {lat, lng} objects
 * @returns {number} Area in square meters
 */
function calculateAreaLocally(coordinates) {
  if (!coordinates || coordinates.length < 3) {
    return 0;
  }

  const R = 6371000; // Earth radius in meters
  const n = coordinates.length;
  
  // Shoelace formula adapted for spherical coordinates
  let area = 0;
  
  // Calculate average latitude for correction factor
  const avgLat = coordinates.reduce((sum, coord) => sum + coord.lat, 0) / n;
  const latCorrection = Math.cos(avgLat * Math.PI / 180);
  
  // Apply shoelace formula
  for (let i = 0; i < n; i++) {
    const j = (i + 1) % n;
    // Convert lat/lng to meters at this latitude
    const lat1 = coordinates[i].lat;
    const lng1 = coordinates[i].lng;
    const lat2 = coordinates[j].lat;
    const lng2 = coordinates[j].lng;
    
    // Convert to meters (approximate)
    const x1 = lng1 * Math.PI / 180 * R * latCorrection;
    const y1 = lat1 * Math.PI / 180 * R;
    const x2 = lng2 * Math.PI / 180 * R * latCorrection;
    const y2 = lat2 * Math.PI / 180 * R;
    
    area += x1 * y2 - x2 * y1;
  }
  
  return Math.abs(area) / 2;
}

/**
 * Calculate area using external API or fallback to local calculation
 * @param {Array} coordinates - Array of {lat, lng} objects
 * @returns {Promise<number>} Area in square meters
 */
export async function calculateArea(coordinates) {
  // If external API is configured, try to use it
  if (AREA_CALCULATION_API_URL) {
    try {
      const response = await fetch(AREA_CALCULATION_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ coordinates }),
      });

      if (response.ok) {
        const data = await response.json();
        if (data.area && typeof data.area === 'number') {
          return data.area;
        }
      }
    } catch (error) {
      console.warn('External area calculation API failed, using local calculation:', error);
    }
  }

  // Fallback to local calculation
  return calculateAreaLocally(coordinates);
}

